import { MainContainer } from "@/components/mainContainer";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  HStack,
  Image,
  Input,
  Select,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import * as Fonts from "../../../components/texts";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { EventHeader } from "@/components/eventHeader";
import * as T from "../../../components/table";
import { NewIcon } from "@/components/icons";
import { MenuActionButton } from "@/components/menuActionButton";
import { MenuPopUp } from "@/components/menuPopUp";
import { useParametrization } from "@/hooks/useParametrization";
import { ParametrizationDataDto } from "@/pages/parameterization/types";
import {
  EntranceByEventDto,
  EntranceCreateDto,
  EventEditIndexDto,
  EventIndexCreateDto,
} from "../types";
import { useEvent } from "@/hooks/useEvent";
import { useCustomToast } from "@/hooks/useToast";
import { useForm } from "react-hook-form";
import LoadingOverlay from "@/components/overlay";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";

const schema = Joi.object({
  situation: Joi.boolean().required().messages({
    "boolean.base": "Situação deve ser informada",
    "any.required": "Situação deve ser informada",
  }),
  name: Joi.string().required().messages({
    "string.empty": "Nome do Evento deve ser informado",
  }),
  branch: Joi.number().required().messages({
    "number.base": "Unidade deve ser informada",
    "any.required": "Unidade deve ser informada",
  }),
  team: Joi.number().required().messages({
    "number.base": "Pacote deve ser informado",
    "any.required": "Pacote deve ser informado",
  }),
  type: Joi.number().required().messages({
    "number.base": "Tipo do Evento deve ser informado",
    "any.required": "Tipo do Evento deve ser informado",
  }),
  pickupInitialDate: Joi.date().required().messages({
    "date.base": "Data do Início das Retiradas deve ser informada",
    "any.required": "Data do Início das Retiradas deve ser informada",
  }),
  pickupFinalDate: Joi.date().required().messages({
    "date.base": "Data do Fim das Retiradas deve ser informada",
    "any.required": "Data do Fim das Retiradas deve ser informada",
  }),
  date: Joi.date().required().messages({
    "date.base": "Data do Evento deve ser informada",
    "any.required": "Data do Evento deve ser informada",
  }),
  local: Joi.string().required().messages({
    "string.empty": "Local do Evento deve ser informado",
  }),
  address: Joi.string().required().messages({
    "string.empty": "Endereço do Evento deve ser informado",
  }),
});

export const EventsForm = () => {
  const [showMenuPopUp, setShowMenuPopUp] = useState<undefined | number>();
  const { getParameterByType } = useParametrization();
  const { getEntrancesByEventId, eventUpdate, eventCreate, getEventById } =
    useEvent();
  const { customToast } = useCustomToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id: routeId } = useParams<{ id?: string }>();
  const [branches, setBranches] = useState<ParametrizationDataDto[]>([]);
  const [teams, setTeams] = useState<ParametrizationDataDto[]>([]);
  const [types, setTypes] = useState<ParametrizationDataDto[]>([]);
  const [formData, setFormData] = useState<EventEditIndexDto>();

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageName, setImageName] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerName, setBannerName] = useState("");
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState("");
  const inputBannerRef = useRef<HTMLInputElement>(null);

  const [entrances, setEntrances] = useState<EntranceByEventDto[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EventIndexCreateDto>({
    resolver: joiResolver(schema, {
      abortEarly: false,
      allowUnknown: true,
    }),
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60 * 1000);
    return localDate.toISOString().slice(0, 16);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response = await getParameterByType();
        setBranches(response.data.branches);
        setTeams(response.data.teams);
        setTypes(response.data.types);

        // Caso 1: veio da lista de eventos com state (fluxo original)
        if (state?.event && typeof state.event.id !== "undefined") {
          setFormData(state.event);
          setEditingId(state.event.id);
          setImageUrl(state.event.invitationUrl);
          // Carrega banner existente se houver
          if (state.event.bannerUrl) setBannerPreviewUrl(state.event.bannerUrl);
          const eventEntrances = await getEntrancesByEventId(state.event.id);
          setEntrances(
            eventEntrances.map((e: any) => ({
              id: e.id,
              type: e.type,
              allowClientGuest: e.allowClientGuest,
              eventId: e.eventId,
            }))
          );
          reset({
            name: state.event.name,
            branch: state.event.branch,
            team: state.event.team,
            situation: state.event.situation,
            type: state.event.type,
            pickupInitialDate: formatDate(state.event.pickupInitialDate),
            pickupFinalDate: formatDate(state.event.pickupFinalDate),
            date: formatDate(state.event.date),
            local: state.event.local,
            address: state.event.address,
            invitationUrl: state.event.invitationUrl,
          });

          // Caso 2: acessou /eventos/editar/:id direto (ex: pelo EventHeader)
        } else if (routeId) {
          const eventResponse = await getEventById(Number(routeId));
          const evt = eventResponse.data;
          setFormData(evt);
          setEditingId(evt.id);
          setImageUrl(evt.invitationUrl ?? "");
          // Carrega banner existente se houver
          if (evt.bannerUrl) setBannerPreviewUrl(evt.bannerUrl);
          const eventEntrances = await getEntrancesByEventId(evt.id);
          setEntrances(
            eventEntrances.map((e: any) => ({
              id: e.id,
              type: e.type,
              allowClientGuest: e.allowClientGuest,
              eventId: e.eventId,
            }))
          );
          reset({
            name: evt.name,
            branch: evt.branch?.id ?? evt.branch,
            team: evt.team?.id ?? evt.team,
            situation: evt.situation,
            type: evt.type?.id ?? evt.type,
            pickupInitialDate: formatDate(
              evt.pickupInitalDate ?? evt.pickupInitialDate
            ),
            pickupFinalDate: formatDate(evt.pickupFinalDate),
            date: formatDate(evt.date),
            local: evt.local,
            address: evt.address,
            invitationUrl: evt.invitationUrl ?? "",
          });
        }
      } catch (error) {
        customToast("error", "Erro ao carregar evento");
      }
    };
    loadInitialData();
  }, [reset, routeId]);

  const entranceHandler = () => {
    setEntrances((prev) => [
      ...prev,
      { type: "", allowClientGuest: false } as EntranceCreateDto,
    ]);
  };

  const addEntrance = <entrances extends keyof EntranceCreateDto>(
    index: number,
    key: entrances,
    value: EntranceCreateDto[entrances]
  ) => {
    setEntrances((prev) => {
      return prev.map((item, i) =>
        i === index ? ({ ...item, [key]: value } as EntranceCreateDto) : item
      );
    });
  };

  const handleCheckboxChange = (index: number) => {
    setEntrances((prevEntrances) =>
      prevEntrances.map((entrance, i) => ({
        ...entrance,
        allowClientGuest: i === index ? !entrance.allowClientGuest : false,
      }))
    );
  };

  // ── Invitation image handler ──
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageName(file.name);
      setImageUrl(URL.createObjectURL(file));
    }
  };

  // ── Banner image handler ──
  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBannerFile(file);
      setBannerName(file.name);
      setBannerPreviewUrl(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    try {
      const submitData = new FormData();
      submitData.append("name", data.name);
      submitData.append("branch", data.branch);
      submitData.append("team", data.team);
      submitData.append("type", data.type);
      submitData.append("situation", data.situation);
      submitData.append(
        "pickupInitialDate",
        new Date(data.pickupInitialDate).toISOString()
      );
      submitData.append(
        "pickupFinalDate",
        new Date(data.pickupFinalDate).toISOString()
      );
      submitData.append("date", new Date(data.date).toISOString());
      submitData.append("local", data.local);
      submitData.append("address", data.address);

      if (imageFile) {
        submitData.append("invitationMedia", imageFile);
      }

      // ── Appenda o banner junto com os outros campos ──
      if (bannerFile) {
        submitData.append("bannerMedia", bannerFile);
      }

      // Adiciona entradas
      entrances.forEach((entrance, index) => {
        if (entrance.id) {
          submitData.append(`entrance[${index}][id]`, String(entrance.id));
        }
        submitData.append(`entrance[${index}][type]`, entrance.type);
        submitData.append(
          `entrance[${index}][allowClientGuest]`,
          String(entrance.allowClientGuest)
        );
      });

      if (editingId !== null) {
        await eventUpdate(editingId, submitData);
        customToast("success", "Evento atualizado com sucesso");
      } else {
        await eventCreate(submitData);
        setEditingId(null);
        customToast("success", "Evento cadastrado com sucesso");
      }

      // Reset
      reset();
      setEntrances([]);
      setImageFile(null);
      setImageUrl("");
      setImageName("");
      setBannerFile(null);
      setBannerPreviewUrl("");
      setBannerName("");
    } catch (error: any) {
      customToast("error", error.response.data.message);
    } finally {
      navigate("/eventos");
      setIsLoading(false);
    }
  };

  return (
    <>
      {routeId && <EventHeader eventName={formData?.name} />}
      <MainContainer height="fit-content">
        {isLoading && <LoadingOverlay />}
        {!isLoading && (
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Stack
              p={3}
              w="100%"
              align="stretch"
              justify="flex-start"
              height="auto"
            >
              <Text
                fontFamily="Inter"
                fontStyle="normal"
                fontWeight="600"
                fontSize="24px"
                lineHeight="31px"
                color="neutral.600"
              >
                {`${formData?.id ? "Editar Evento" : "Cadastrar Evento"}`}
              </Text>
              <Text>
                {`${formData?.id ? "" : "Preencha para criar o Evento"}`}
              </Text>
              <Stack spacing={3} height="auto">
                {/* ── Informações Básicas ── */}
                <Box
                  bgColor="neutral.100"
                  borderRadius={5}
                  p={3}
                  alignSelf="flex-start"
                  w="100%"
                >
                  <HStack w="100%" spacing={4} mt={3}>
                    <Stack w="75%">
                      <Text
                        fontFamily="Inter"
                        fontStyle="normal"
                        fontWeight="600"
                        fontSize="24px"
                        lineHeight="31px"
                        color="neutral.600"
                      >
                        Informações Básicas
                      </Text>
                      <Text>Insira os dados básicos.</Text>
                    </Stack>
                    <FormControl w="25%">
                      <Fonts.FormLabelText>Status*</Fonts.FormLabelText>
                      <Select
                        defaultValue={
                          formData
                            ? formData.situation
                              ? "true"
                              : "false"
                            : "true"
                        }
                        h={10}
                        borderRadius={4}
                        bgColor="white"
                        {...register("situation")}
                      >
                        <option value="true">Aberto</option>
                        <option value="false">Cancelado</option>
                      </Select>
                      {errors?.situation?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.situation.message}
                        </Text>
                      )}
                    </FormControl>
                  </HStack>
                  <HStack w="100%" spacing={4} mt={3}>
                    <FormControl w="50%">
                      <Fonts.FormLabelText>Nome do evento*</Fonts.FormLabelText>
                      <Input
                        defaultValue={formData?.name}
                        type="text"
                        textIndent={"1px"}
                        w="100%"
                        h={10}
                        borderRadius={3}
                        border="1px solid #EAEAEA"
                        bgColor="neutral.0"
                        placeholder="Insira o nome do evento."
                        {...register("name")}
                      />
                      {errors?.name?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.name.message}
                        </Text>
                      )}
                    </FormControl>
                    <FormControl w="25%">
                      <Fonts.FormLabelText>Unidade*</Fonts.FormLabelText>
                      <Select
                        h={10}
                        borderRadius={4}
                        bgColor="white"
                        {...register("branch")}
                      >
                        <option value={""}>Selecione a Unidade</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </Select>
                      {errors?.branch?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.branch.message}
                        </Text>
                      )}
                    </FormControl>
                    <FormControl w="25%">
                      <Fonts.FormLabelText>Pacote*</Fonts.FormLabelText>
                      <Select
                        h={10}
                        borderRadius={4}
                        bgColor="white"
                        {...register("team")}
                      >
                        <option value={""}>Selecione o Pacote</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.name}
                          </option>
                        ))}
                      </Select>
                      {errors?.team?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.team.message}
                        </Text>
                      )}
                    </FormControl>
                  </HStack>
                  <HStack w="100%" spacing={4} mt={3}>
                    <FormControl w="25%">
                      <Fonts.FormLabelText>Tipo do Evento*</Fonts.FormLabelText>
                      <Select
                        h={10}
                        borderRadius={4}
                        bgColor="white"
                        {...register("type")}
                      >
                        <option value={""}>Selecione o Tipo</option>
                        {types.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </Select>
                      {errors?.type?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.type.message}
                        </Text>
                      )}
                    </FormControl>
                    <FormControl w="25%">
                      <Fonts.FormLabelText>
                        Data de Início das Retiradas*
                      </Fonts.FormLabelText>
                      <Input
                        type="datetime-local"
                        textIndent={"1px"}
                        w="100%"
                        h={10}
                        borderRadius={3}
                        border="1px solid #EAEAEA"
                        bgColor="neutral.0"
                        {...register("pickupInitialDate")}
                      />
                      {errors?.pickupInitialDate?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.pickupInitialDate.message}
                        </Text>
                      )}
                    </FormControl>
                    <FormControl w="25%">
                      <Fonts.FormLabelText>
                        Data de Fim das Retiradas*
                      </Fonts.FormLabelText>
                      <Input
                        type="datetime-local"
                        textIndent={"1px"}
                        w="100%"
                        h={10}
                        borderRadius={3}
                        border="1px solid #EAEAEA"
                        bgColor="neutral.0"
                        {...register("pickupFinalDate")}
                      />
                      {errors?.pickupFinalDate?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.pickupFinalDate.message}
                        </Text>
                      )}
                    </FormControl>
                    <FormControl w="25%">
                      <Fonts.FormLabelText>Data do Evento</Fonts.FormLabelText>
                      <Input
                        type="datetime-local"
                        textIndent={"1px"}
                        w="100%"
                        h={10}
                        borderRadius={3}
                        border="1px solid #EAEAEA"
                        bgColor="neutral.0"
                        {...register("date")}
                      />
                      {errors?.date?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.date.message}
                        </Text>
                      )}
                    </FormControl>
                  </HStack>
                </Box>

                {/* ── Endereço ── */}
                <Box bgColor="neutral.100" borderRadius={5} p={3}>
                  <HStack w="100%" spacing={4} mt={3}>
                    <Stack w="100%">
                      <Text
                        fontFamily="Inter"
                        fontStyle="normal"
                        fontWeight="600"
                        fontSize="24px"
                        lineHeight="31px"
                        color="neutral.600"
                      >
                        Endereço do Evento
                      </Text>
                      <Text>Insira os dados de localização.</Text>
                    </Stack>
                  </HStack>
                  <HStack w="100%" spacing={4} mt={3}>
                    <FormControl w="40%">
                      <Fonts.FormLabelText>Local</Fonts.FormLabelText>
                      <Input
                        type="text"
                        textIndent={"1px"}
                        w="100%"
                        h={10}
                        borderRadius={3}
                        border="1px solid #EAEAEA"
                        bgColor="neutral.0"
                        placeholder="Insira o local do evento."
                        {...register("local")}
                      />
                      {errors?.local?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.local.message}
                        </Text>
                      )}
                    </FormControl>
                    <FormControl w="60%">
                      <Fonts.FormLabelText>Endereço</Fonts.FormLabelText>
                      <Input
                        type="text"
                        textIndent={"1px"}
                        w="100%"
                        h={10}
                        borderRadius={3}
                        border="1px solid #EAEAEA"
                        bgColor="neutral.0"
                        placeholder="Insira o endereço do evento."
                        {...register("address")}
                      />
                      {errors?.address?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {errors.address.message}
                        </Text>
                      )}
                    </FormControl>
                  </HStack>
                </Box>

                {/* ── Entradas ── */}
                <Box bgColor="neutral.100" borderRadius={5} p={3}>
                  <HStack w="100%" spacing={4} mt={3}>
                    <Stack w="85%">
                      <Text
                        fontFamily="Inter"
                        fontStyle="normal"
                        fontWeight="600"
                        fontSize="24px"
                        lineHeight="31px"
                        color="neutral.600"
                      >
                        Entradas
                      </Text>
                      <Text>Lotes de convites.</Text>
                    </Stack>
                    <Stack w="15%">
                      <Button
                        leftIcon={<NewIcon />}
                        variant="solid"
                        bgColor="secondary.500"
                        h={9}
                        onClick={entranceHandler}
                        color="white"
                        borderRadius={4}
                        _hover={{ bgColor: "secondary.300" }}
                        _active={{ bgColor: "secondary.500" }}
                      >
                        Nova Entrada
                      </Button>
                    </Stack>
                  </HStack>
                  <HStack w="100%" spacing={4} mt={3}>
                    <Stack p={2} mt="0 !important" w="100%">
                      <TableContainer w="100%">
                        <Table w="100%">
                          <Thead w="100%">
                            <Th textTransform="none" w="45%">
                              <Tr fontSize="15px">Lote</Tr>
                            </Th>
                            <Th textTransform="none">
                              <Tr fontSize="15px" w="20%">
                                Cliente Convidado
                              </Tr>
                              <Text fontSize="12px" fontWeight="10">
                                Ao marcar Cliente como convidado, ele receberá
                                automaticamente um convite em seu nome para o
                                evento e lote em questão.
                              </Text>
                            </Th>
                            <Th>
                              <Tr></Tr>
                            </Th>
                          </Thead>
                          <Tbody w="100%">
                            {entrances.map((entrance, index) => (
                              <T.Body key={index}>
                                <Td w="20%">
                                  <Input
                                    placeholder="Adicione um lote"
                                    value={entrance.type}
                                    onChange={(e) =>
                                      addEntrance(index, "type", e.target.value)
                                    }
                                  />
                                </Td>
                                <Td>
                                  <HStack spacing={4}>
                                    <Checkbox
                                      isChecked={entrance.allowClientGuest}
                                      onChange={() =>
                                        handleCheckboxChange(index)
                                      }
                                    >
                                      Atribuir convite ao Cliente?
                                    </Checkbox>
                                    <Text fontWeight="medium">
                                      {entrance.allowClientGuest
                                        ? "Sim"
                                        : "Não"}
                                    </Text>
                                  </HStack>
                                </Td>
                                <Td>
                                  <MenuActionButton
                                    w="10%"
                                    color="#2F2F2F"
                                    onClick={() => setShowMenuPopUp(index)}
                                    _hover={{ bgColor: "neutral.200" }}
                                    _active={{ bgColor: "neutral.300" }}
                                  />
                                </Td>
                                {showMenuPopUp === index && (
                                  <MenuPopUp
                                    top="-4px"
                                    right="35px"
                                    options={[
                                      {
                                        title: "Atribuir Convite ao Cliente",
                                        onClick: () =>
                                          handleCheckboxChange(index),
                                      },
                                    ]}
                                    setShowMenuPopUp={setShowMenuPopUp}
                                  />
                                )}
                              </T.Body>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  </HStack>
                </Box>

                {/* ── Banner do Evento ── */}
                <Box bgColor="neutral.100" borderRadius={5} p={3}>
                  <HStack w="100%" spacing={4} mt={3}>
                    <Stack w="100%">
                      <Text
                        fontFamily="Inter"
                        fontStyle="normal"
                        fontWeight="600"
                        fontSize="24px"
                        lineHeight="31px"
                        color="neutral.600"
                      >
                        Banner do Evento
                      </Text>
                      <Text>Imagem exibida no card do evento.</Text>
                    </Stack>
                  </HStack>
                  <Flex p={2} mt="0 !important" w="100%" gap={4}>
                    {/* Controles de upload */}
                    <Box
                      w="50%"
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      gap={3}
                    >
                      <FormControl>
                        <Text color="neutral.500" fontWeight="semibold" mb={1}>
                          Selecione o banner
                        </Text>
                        {/* Input oculto — mesmo padrão do convite */}
                        <input
                          ref={inputBannerRef}
                          type="file"
                          style={{ display: "none" }}
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleBannerChange}
                        />
                        {/* Input visível para exibir o nome */}
                        <Input
                          value={bannerName}
                          placeholder="Selecione sua imagem."
                          isReadOnly
                          w="100%"
                          cursor="pointer"
                          onClick={() => inputBannerRef.current?.click()}
                          mb={2}
                        />
                        <Button
                          onClick={() => inputBannerRef.current?.click()}
                          variant="solid"
                          bgColor="secondary.500"
                          width="100px"
                          color="white"
                          _hover={{ bgColor: "secondary.300" }}
                          _active={{ bgColor: "secondary.500" }}
                        >
                          Buscar
                        </Button>
                        <Text fontSize="13px" fontWeight="semibold" mt={2}>
                          Dimensão recomendada: 1200 × 400px · JPG ou PNG · máx.
                          500KB
                        </Text>
                      </FormControl>
                    </Box>

                    {/* Pré-visualização do banner */}
                    <Box
                      w="50%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="md"
                      overflow="hidden"
                      border="1px solid"
                      borderColor="neutral.200"
                      bg="neutral.700"
                      minH="140px"
                      position="relative"
                    >
                      {bannerPreviewUrl ? (
                        <Image
                          src={bannerPreviewUrl}
                          alt="Preview do banner"
                          w="100%"
                          h="100%"
                          objectFit="cover"
                        />
                      ) : (
                        <Text color="neutral.400" fontSize="13px">
                          Nenhum banner carregado
                        </Text>
                      )}
                    </Box>
                  </Flex>
                </Box>

                {/* ── Arte do Convite ── */}
                <Box bgColor="neutral.100" borderRadius={5} p={3}>
                  <Stack w="100%">
                    <Text
                      fontFamily="Inter"
                      fontStyle="normal"
                      fontWeight="600"
                      fontSize="24px"
                      lineHeight="31px"
                      color="neutral.600"
                    >
                      Convite
                    </Text>
                    <Text>Insira os dados do convite.</Text>
                  </Stack>
                  <Flex p={2} mt="0 !important" w="100%" gap={4}>
                    <Box
                      w="50%"
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                    >
                      <FormControl>
                        <Text color="neutral.500" fontWeight="semibold">
                          Insira a Arte do Convite
                        </Text>
                        <Input
                          value={imageName}
                          placeholder="Selecione sua imagem."
                          isReadOnly
                          w="40%"
                          onClick={() => {
                            const input = document.getElementById("fileInput");
                            if (input) input.click();
                          }}
                        />
                        <Button
                          onClick={() => {
                            const input = document.getElementById("fileInput");
                            if (input) input.click();
                          }}
                          variant="solid"
                          bgColor="secondary.500"
                          mb="5px"
                          width="100px"
                          color="white"
                          _hover={{ bgColor: "secondary.300" }}
                          _active={{ bgColor: "secondary.500" }}
                        >
                          Buscar
                        </Button>
                        <input
                          type="file"
                          id="fileInput"
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                        <Text fontSize="14px" fontWeight="semibold">
                          Dimensão recomendada: 750 x 400, meio em branco para o
                          QRcode.
                        </Text>
                      </FormControl>
                    </Box>
                    <Box
                      w="50%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {imageUrl ? (
                        <Image
                          src={imageUrl}
                          alt="Imagem carregada"
                          maxHeight="100%"
                          maxWidth="100%"
                          objectFit="contain"
                        />
                      ) : (
                        <Text color="gray.500">Nenhuma imagem carregada</Text>
                      )}
                    </Box>
                  </Flex>
                </Box>

                {/* ── Botões ── */}
                <Stack>
                  <HStack spacing={4} justifyContent="center" mt={7} mb={3}>
                    <Box w="25%"></Box>
                    <Button
                      variant="outline"
                      borderColor="secondary.500"
                      colorScheme="secondary.500"
                      w="25%"
                      h={9}
                      borderRadius={4}
                      onClick={() => navigate("/eventos")}
                      _hover={{ bgColor: "secondary.100" }}
                      _active={{ bgColor: "secondary.300" }}
                    >
                      <Fonts.ButtonText color="secondary.500">
                        Cancelar
                      </Fonts.ButtonText>
                    </Button>
                    <Button
                      variant="solid"
                      type="submit"
                      bgColor="secondary.500"
                      w="25%"
                      h={9}
                      borderRadius={4}
                      _hover={{ bgColor: "secondary.300" }}
                      _active={{ bgColor: "secondary.500" }}
                    >
                      <Fonts.ButtonText>Salvar Alterações</Fonts.ButtonText>
                    </Button>
                    <Box w="25%"></Box>
                  </HStack>
                </Stack>
              </Stack>
            </Stack>
          </form>
        )}
      </MainContainer>
    </>
  );
};
